import React, { useState, useMemo } from "react";
import { X, Save, Send, Eye, EyeOff, Clock, FileText, Search, Languages, ExternalLink, Sparkles, ImagePlus, Images, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/admin/ImageUpload";
import RichTextEditor from "@/components/admin/RichTextEditor";
import RichText from "@/components/RichText";
import { api, mediaUrl } from "@/lib/api";
import { IMG } from "@/lib/content";

const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").slice(0, 80);
const words = (html) => (html || "").replace(/<[^>]*>/g, " ").split(/\s+/).filter(Boolean).length;

const TABS = [
  ["content", "Content", FileText],
  ["meta", "Details & SEO", Search],
  ["publish", "Publishing", Clock],
];

const SITE_PHOTOS = Object.entries(IMG).map(([k, url]) => ({ label: k.replace(/([A-Z])/g, " $1"), url }));

function Sheet({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-[90] bg-obsidian/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-3 sm:p-6" data-testid={testid}>
      <div className="bg-charcoal border border-primary-gold/20 rounded-2xl w-full max-w-xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h3 className="text-base font-light text-ivory">{title}</h3>
          <button onClick={onClose} className="text-muted-text hover:text-ivory p-1" data-testid={`${testid}-close`}><X size={18} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export default function InsightEditor({ initial, categories = [], onSave, onClose }) {
  const [d, setD] = useState(() => ({ published: true, category: "Perspective", author: "Intersect Holding", ...initial }));
  const [tab, setTab] = useState("content");
  const [lang, setLang] = useState("en");
  const [preview, setPreview] = useState(true);
  const [saving, setSaving] = useState("");
  const [busy, setBusy] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiLength, setAiLength] = useState("short");
  const [photoOpen, setPhotoOpen] = useState(false);
  const [newCat, setNewCat] = useState(false);
  const [library, setLibrary] = useState([]);

  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const isAr = lang === "ar";
  const wc = words(isAr ? d.content_ar : d.content);
  const slug = d.slug || slugify(d.title);

  const catOptions = useMemo(() => {
    const base = ["Investment", "Saudi Arabia", "Venture Capital", "Markets", "Partnerships", "Technology", "Perspective"];
    return Array.from(new Set([...base, ...categories.filter(Boolean), d.category].filter(Boolean)));
  }, [categories, d.category]);

  const status = useMemo(() => {
    if (!d.published) return { label: "Draft", cls: "text-amber-300" };
    if (d.publish_at && new Date(d.publish_at) > new Date()) return { label: "Scheduled", cls: "text-sky-300" };
    return { label: "Published", cls: "text-emerald-300" };
  }, [d.published, d.publish_at]);

  const submit = async (mode) => {
    if (!d.title) { toast.error("A title is required."); return; }
    setSaving(mode);
    const payload = {
      ...d,
      slug,
      published: mode === "publish" ? true : mode === "draft" ? false : d.published,
      reading_time: d.reading_time || `${Math.max(1, Math.round(wc / 220))} min read`,
    };
    if (mode === "publish") payload.publish_at = d.publish_at || null;
    try { await onSave(payload); } finally { setSaving(""); }
  };

  const generate = async () => {
    if (!aiTopic.trim()) { toast.error("Describe the subject first."); return; }
    setBusy("draft");
    try {
      const { data } = await api.post("/admin/ai/draft", { topic: aiTopic, category: d.category, length: aiLength });
      setD((p) => ({
        ...p,
        title: data.title || p.title,
        excerpt: data.excerpt || p.excerpt,
        content: data.content || p.content,
        title_ar: data.title_ar || p.title_ar,
        excerpt_ar: data.excerpt_ar || p.excerpt_ar,
        content_ar: data.content_ar || p.content_ar,
        category: data.category || p.category,
        seo_title: data.seo_title || p.seo_title,
        seo_description: data.seo_description || p.seo_description,
        image_prompt: data.image_prompt || p.image_prompt,
      }));
      setAiOpen(false);
      setLang("en");
      toast.success("Draft generated in English and Arabic. Review before publishing.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed."); } finally { setBusy(""); }
  };

  const translate = async () => {
    if (!d.title && !d.content) { toast.error("Write the English version first."); return; }
    setBusy("translate");
    try {
      const { data } = await api.post("/admin/ai/translate", { title: d.title, excerpt: d.excerpt, content: d.content });
      setD((p) => ({ ...p, title_ar: data.title_ar || p.title_ar, excerpt_ar: data.excerpt_ar || p.excerpt_ar, content_ar: data.content_ar || p.content_ar }));
      setLang("ar");
      toast.success("Arabic version ready. Review the translation.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Translation failed."); } finally { setBusy(""); }
  };

  const makeImage = async () => {
    const prompt = d.image_prompt || d.title;
    if (!prompt) { toast.error("Add a title first so the image matches the subject."); return; }
    setBusy("image");
    try {
      const { data } = await api.post("/admin/ai/image", { prompt });
      set("hero_image", data.url);
      toast.success("Hero image generated.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Image generation failed."); } finally { setBusy(""); }
  };

  const openPhotos = async () => {
    setPhotoOpen(true);
    try { const { data } = await api.get("/admin/media"); setLibrary(data.filter((m) => !SITE_PHOTOS.some((p) => p.url === m.url))); } catch { setLibrary([]); }
  };

  const field = (k, label, type = "text", extra) => (
    <div key={k}>
      <label className="eyebrow block mb-2">{label}</label>
      {type === "textarea"
        ? <textarea rows={3} className={`field resize-none text-sm ${k.endsWith("_ar") ? "ar-font text-right" : ""}`} data-testid={`field-${k}`} dir={k.endsWith("_ar") ? "rtl" : "ltr"} value={d[k] || ""} onChange={(e) => set(k, e.target.value)} />
        : <input type={type} className={`field text-sm ${k.endsWith("_ar") ? "ar-font text-right" : ""}`} data-testid={`field-${k}`} dir={k.endsWith("_ar") ? "rtl" : "ltr"} value={d[k] || ""} onChange={(e) => set(k, e.target.value)} />}
      {extra && <p className="text-[11px] text-muted-text mt-1">{extra}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[80] bg-obsidian/95 backdrop-blur-sm overflow-y-auto" data-testid="insight-editor">
      <div className="sticky top-0 z-10 bg-obsidian/95 backdrop-blur border-b border-primary-gold/15 px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
        <button onClick={onClose} className="text-muted-text hover:text-ivory p-1" data-testid="insight-editor-close" aria-label="Close"><X size={20} /></button>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-ivory truncate">{d.title || "New insight"}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`a-chip ${status.cls}`} data-testid="insight-status">{status.label}</span>
            <span className="text-[11px] text-muted-text">{wc} words</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {slug && d.id && (
            <a href={`/insights/${slug}`} target="_blank" rel="noreferrer" className="text-muted-text hover:text-primary-gold p-2" title="Open on site" data-testid="insight-open-site"><ExternalLink size={16} /></a>
          )}
          <button onClick={() => setPreview((v) => !v)} className="btn-gold btn-sm" data-testid="insight-toggle-preview">
            {preview ? <EyeOff size={14} /> : <Eye size={14} />} Preview
          </button>
          <button onClick={() => submit("draft")} disabled={!!saving} className="btn-gold btn-sm" data-testid="insight-save-draft">
            <Save size={14} /> {saving === "draft" ? "Saving…" : "Save draft"}
          </button>
          <button onClick={() => submit("publish")} disabled={!!saving} className="btn-solid btn-sm" data-testid="insight-publish">
            <Send size={14} /> {saving === "publish" ? "Publishing…" : d.publish_at ? "Schedule" : "Publish"}
          </button>
        </div>
      </div>

      <div className={`p-4 sm:p-6 grid gap-6 ${preview ? "xl:grid-cols-2" : "max-w-3xl mx-auto"}`}>
        <div className="space-y-6 min-w-0">
          <div className="flex flex-wrap gap-2 items-center">
            <button onClick={() => setAiOpen(true)} className="btn-solid btn-sm" data-testid="insight-ai-suggest"><Sparkles size={14} /> Suggest article</button>
            <button onClick={translate} disabled={!!busy} className="btn-gold btn-sm" data-testid="insight-ai-translate">
              {busy === "translate" ? <Loader2 size={14} className="animate-spin" /> : <Languages size={14} />} Translate to Arabic
            </button>
            <button onClick={makeImage} disabled={!!busy} className="btn-gold btn-sm" data-testid="insight-ai-image">
              {busy === "image" ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Generate image
            </button>
            <button onClick={openPhotos} className="btn-gold btn-sm" data-testid="insight-find-photo"><Images size={14} /> Find photo</button>
          </div>

          <div className="flex gap-2 flex-wrap">
            {TABS.map(([k, label, Icon]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`insight-tab-${k}`}
                className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wide rounded-full border transition-colors ${tab === k ? "border-primary-gold text-primary-gold bg-primary-gold/10" : "border-white/10 text-muted-text hover:text-soft-stone"}`}>
                <Icon size={13} strokeWidth={1.5} /> {label}
              </button>
            ))}
            <button onClick={() => setLang(isAr ? "en" : "ar")} className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wide rounded-full border border-white/10 text-muted-text hover:text-primary-gold ml-auto" data-testid="insight-lang-toggle">
              <Languages size={13} strokeWidth={1.5} /> {isAr ? "العربية" : "English"}
            </button>
          </div>

          {tab === "content" && (
            <div className="space-y-5">
              {isAr ? field("title_ar", "Title (Arabic)") : field("title", "Title")}
              {isAr ? field("excerpt_ar", "Excerpt (Arabic)", "textarea") : field("excerpt", "Excerpt", "textarea", "Shown on the insights list and used for social sharing.")}
              <div>
                <label className="eyebrow block mb-2">{isAr ? "Body (Arabic)" : "Body"}</label>
                <RichTextEditor
                  key={lang}
                  value={(isAr ? d.content_ar : d.content) || ""}
                  onChange={(v) => set(isAr ? "content_ar" : "content", v)}
                  dir={isAr ? "rtl" : "ltr"}
                  testid={`field-${isAr ? "content_ar" : "content"}`}
                />
                {!isAr && <p className="text-[11px] text-muted-text mt-1">If the Arabic version is left empty, it is translated automatically when you publish.</p>}
              </div>
              <ImageUpload label="Hero Image" value={d.hero_image} onChange={(v) => set("hero_image", v)} testid="field-hero_image" />
            </div>
          )}

          {tab === "meta" && (
            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="eyebrow block mb-2">Category</label>
                  {newCat ? (
                    <div className="flex gap-2">
                      <input className="field text-sm" autoFocus placeholder="New category" data-testid="field-category-new"
                        value={d.category || ""} onChange={(e) => set("category", e.target.value)} />
                      <button type="button" onClick={() => setNewCat(false)} className="btn-gold btn-sm" data-testid="field-category-done">Done</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <select className="field text-sm" data-testid="field-category" value={d.category || ""} onChange={(e) => set("category", e.target.value)}>
                        {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <button type="button" onClick={() => { setNewCat(true); set("category", ""); }} className="btn-gold btn-sm" title="Add new category" data-testid="field-category-add"><Plus size={14} /></button>
                    </div>
                  )}
                </div>
                {field("author", "Author")}
                {field("reading_time", "Reading Time", "text", `Auto: ~${Math.max(1, Math.round(wc / 220))} min read`)}
                {field("date", "Display Date", "text", "e.g. June 12, 2026 — leave blank for today")}
              </div>
              <div>
                <label className="eyebrow block mb-2">URL Slug</label>
                <input className="field text-sm" data-testid="field-slug" value={d.slug || ""} placeholder={slugify(d.title)} onChange={(e) => set("slug", slugify(e.target.value))} />
                <p className="text-[11px] text-muted-text mt-1">/insights/{slug || "…"}</p>
              </div>
              {field("seo_title", "SEO Title", "text", `${(d.seo_title || d.title || "").length}/60 characters`)}
              {field("seo_description", "SEO Description", "textarea", `${(d.seo_description || d.excerpt || "").length}/160 characters`)}
              {field("image_prompt", "Image Brief (used by “Generate image”)", "textarea")}
            </div>
          )}

          {tab === "publish" && (
            <div className="space-y-6">
              <div className="a-card p-5 space-y-4">
                <label className="flex items-center justify-between gap-4 cursor-pointer">
                  <span className="text-sm text-soft-stone">Published on the live site</span>
                  <input type="checkbox" className="w-5 h-5 accent-[#C6A15B]" checked={!!d.published} onChange={(e) => set("published", e.target.checked)} data-testid="field-published" />
                </label>
                <div>
                  <label className="eyebrow block mb-2">Publish Date &amp; Time (optional)</label>
                  <input type="datetime-local" className="field text-sm" data-testid="field-publish_at"
                    value={d.publish_at ? d.publish_at.slice(0, 16) : ""}
                    onChange={(e) => set("publish_at", e.target.value ? new Date(e.target.value).toISOString() : null)} />
                  <p className="text-[11px] text-muted-text mt-1">Set a future time to schedule; the article stays hidden until then.</p>
                </div>
                {d.publish_at && (
                  <button onClick={() => set("publish_at", null)} className="text-xs text-muted-text hover:text-primary-gold" data-testid="insight-clear-schedule">Clear schedule</button>
                )}
              </div>
              <p className="text-xs text-muted-text">Current state: <span className={status.cls}>{status.label}</span></p>
            </div>
          )}
        </div>

        {preview && (
          <div className="a-card p-5 xl:sticky xl:top-24 h-fit min-w-0" data-testid="insight-preview">
            <p className="text-xs uppercase tracking-wide text-primary-gold mb-4">Preview · {isAr ? "العربية" : "English"}</p>
            <div className="max-h-[70vh] overflow-y-auto pr-2" dir={isAr ? "rtl" : "ltr"}>
              {d.hero_image && <img src={mediaUrl(d.hero_image)} alt="" className="w-full h-44 object-cover rounded-lg mb-5" />}
              <p className="eyebrow mb-3">{d.category} {d.reading_time ? `· ${d.reading_time}` : ""}</p>
              <h2 className={`text-2xl md:text-3xl font-light text-ivory mb-3 ${isAr ? "ar-font" : ""}`}>{(isAr ? d.title_ar : d.title) || "Untitled insight"}</h2>
              <p className="text-sm text-muted-text mb-6">{d.author} {d.date ? `· ${d.date}` : ""}</p>
              {(isAr ? d.excerpt_ar : d.excerpt) && <p className={`text-base text-soft-stone mb-6 ${isAr ? "ar-font" : "serif-italic"}`}>{isAr ? d.excerpt_ar : d.excerpt}</p>}
              <RichText html={isAr ? d.content_ar : d.content} className={`text-soft-stone text-sm ${isAr ? "ar-font" : ""}`} />
            </div>
          </div>
        )}
      </div>

      {aiOpen && (
        <Sheet title="Suggest an article" onClose={() => setAiOpen(false)} testid="ai-sheet">
          <div className="space-y-5">
            <div>
              <label className="eyebrow block mb-2">What should it be about?</label>
              <textarea rows={3} className="field resize-none text-sm" autoFocus data-testid="ai-topic"
                placeholder="e.g. Why Saudi family offices are moving into venture capital"
                value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} />
            </div>
            <div>
              <label className="eyebrow block mb-2">Length</label>
              <div className="flex gap-2">
                {[["short", "Short (1–2 paragraphs)"], ["medium", "Standard (4–5 paragraphs)"]].map(([k, l]) => (
                  <button key={k} onClick={() => setAiLength(k)} data-testid={`ai-length-${k}`}
                    className={`px-4 py-2 text-xs rounded-full border transition-colors ${aiLength === k ? "border-primary-gold text-primary-gold bg-primary-gold/10" : "border-white/10 text-muted-text hover:text-soft-stone"}`}>{l}</button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-muted-text">Writes the article in English and Arabic, suggests a category, SEO fields and an image brief. Everything stays editable.</p>
            <div className="flex gap-3">
              <button onClick={generate} disabled={!!busy} className="btn-solid btn-sm" data-testid="ai-generate">
                {busy === "draft" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {busy === "draft" ? "Writing…" : "Generate"}
              </button>
              <button onClick={() => setAiOpen(false)} className="btn-gold btn-sm">Cancel</button>
            </div>
          </div>
        </Sheet>
      )}

      {photoOpen && (
        <Sheet title="Find a photo" onClose={() => setPhotoOpen(false)} testid="photo-sheet">
          <div className="space-y-6 max-h-[65vh] overflow-y-auto">
            {library.length > 0 && (
              <div>
                <p className="eyebrow mb-3">Uploaded &amp; generated</p>
                <div className="grid grid-cols-3 gap-3">
                  {library.map((m) => (
                    <button key={m.url} onClick={() => { set("hero_image", m.url); setPhotoOpen(false); toast.success("Hero image set."); }}
                      className="group relative aspect-video overflow-hidden rounded-lg border border-white/10 hover:border-primary-gold" data-testid="photo-option">
                      <img src={mediaUrl(m.url)} alt="" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <p className="eyebrow mb-3">Saudi photo library</p>
              <div className="grid grid-cols-3 gap-3">
                {SITE_PHOTOS.map((p) => (
                  <button key={p.url} onClick={() => { set("hero_image", p.url); setPhotoOpen(false); toast.success("Hero image set."); }}
                    className="group relative aspect-video overflow-hidden rounded-lg border border-white/10 hover:border-primary-gold" data-testid="photo-option">
                    <img src={p.url} alt={p.label} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                    <span className="absolute inset-x-0 bottom-0 bg-obsidian/80 text-[10px] text-soft-stone capitalize px-1 py-0.5 truncate">{p.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Sheet>
      )}
    </div>
  );
}
